import CopilotWidget from '../components/CopilotWidget'
/**
 * CoConnect — Finance Dashboard (v1.5.2)
 * ─────────────────────────────────────────────
 * Dedicated portal for the `finance` role. Previously finance users were
 * redirected to the read-only MEL Dashboard. Now they have a full
 * operational portal with 5 tabs:
 *   1. Overview  — KPIs (pending payments, today's disbursements, Momo balance)
 *   2. Payments  — approve batch payments, trigger mobile money
 *   3. Withdrawals — process farmer withdrawal requests
 *   4. Transactions — full transaction ledger with filters
 *   5. Reports   — export financial reports (CSV), reconciliation
 *
 * v1.7.3 FINANCE PATCH (additive — original logic preserved 100%):
 *   C-01: Confirmation dialog before Approve & Disburse (irreversible mobile money payment)
 *   C-02: Confirmation dialog before Approve/Reject withdrawal
 *   M-01: WCAG 2.2 AA skip-to-content link
 *   M-02: Search/filter on Payments, Withdrawals, and Transactions tables
 *   M-03: Loading skeleton when switching tabs
 *   M-04: Graceful fallback when /mel/dashboard fails (overview still renders)
 *   M-05: Parallel fetch of /chain/stats for pending decisions counter badge
 *   M-06: Export buttons show error toast on failure instead of silent window.open
 *   L-01: Date filter on Transactions tab (last 7/30/90 days + all-time)
 *   L-02: Price per kg validation (must be positive number)
 *   L-03: Withdrawals tab badge shows today's processed count
 *   L-04: Transactions Method column normalized (carrier || method || type → 'method' fallback)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import {
  BrandMark, Card, SectionHeader, MetricTile, SmartStatus, StatusPill,
  Spinner, EmptyState, Alert, IconButton, SegmentedTabs, Sheet, Pagination, usePagination,
  Skeleton,
} from '../components/ui';

const Icon = {
  Dollar: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>),
  Wallet: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 12V7H5a2 2 0 010-4h14v4M3 5v14a2 2 0 002 2h16v-5M18 12a2 2 0 000 4h4v-4z" /></svg>),
  Trending: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 6l-9.5 9.5-5-5L1 18M17 6h6v6" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3" /></svg>),
  X: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 6L6 18M6 6l12 12" /></svg>),
  Download: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  // v1.7.3 (M-02): search icon for table filters
  Search: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>),
  // v1.7.3 (M-06): warning icon for export error states
  Alert: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" /></svg>),
};

type Tab = 'overview' | 'payments' | 'withdrawals' | 'transactions' | 'reports' | 'ai-insights'; // v2.5

const FinanceDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data state
  const [stats, setStats] = useState<any>(null);
  const [pendingBatches, setPendingBatches] = useState<any[]>([]);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [pricePerKg, setPricePerKg] = useState<string>('2500');

  // Sheet state
  const [paymentSheet, setPaymentSheet] = useState<any | null>(null);
  const [withdrawalSheet, setWithdrawalSheet] = useState<any | null>(null);

  // v1.7.3 (M-02): search filters for the three list tables
  const [paymentsSearch, setPaymentsSearch] = useState('');
  const [withdrawalsSearch, setWithdrawalsSearch] = useState('');
  const [transactionsSearch, setTransactionsSearch] = useState('');

  // v1.7.3 (M-04): flag to render overview even when /mel/dashboard fails
  const [statsError, setStatsError] = useState(false);

  // v1.7.3 (M-05): chain stats — parallel non-blocking fetch for pending decisions counter
  const [chainStats, setChainStats] = useState<any | null>(null);

  // v1.7.3 (L-01): date filter on Transactions tab (default: all-time)
  const [txDateFilter, setTxDateFilter] = useState<'all' | '7' | '30' | '90'>('all');

  // v1.7.3 (M-03): per-tab loading skeleton
  const [tabLoading, setTabLoading] = useState(false);

  // v2.5: AI Insights tab state — 3 AI agents (Market, Finance, Executive)
  const [marketResult, setMarketResult] = useState<any>(null); // v2.5
  const [marketLoading, setMarketLoading] = useState(false); // v2.5
  const [marketError, setMarketError] = useState<string | null>(null); // v2.5
  const [financeInput, setFinanceInput] = useState<{ areaHectares: string; yieldKgPerHa: string; pricePerKg: string }>({ areaHectares: '1', yieldKgPerHa: '400', pricePerKg: '2500' }); // v2.5
  const [financeResult, setFinanceResult] = useState<any>(null); // v2.5
  const [financeLoading, setFinanceLoading] = useState(false); // v2.5
  const [financeError, setFinanceError] = useState<string | null>(null); // v2.5
  const [executiveResult, setExecutiveResult] = useState<any>(null); // v2.5
  const [executiveLoading, setExecutiveLoading] = useState(false); // v2.5
  const [executiveError, setExecutiveError] = useState<string | null>(null); // v2.5

  // v2.5: Market Intelligence handler → POST /api/ai-agents/MARKET/run with {region: 'Cameroon'}
  const handleAnalyzeMarket = async () => { // v2.5
    setMarketLoading(true); setMarketError(null); setMarketResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/MARKET/run', { region: 'Cameroon' }); // v2.5
      setMarketResult(res.data); // v2.5
    } catch (err: any) { setMarketError(err.response?.data?.error || err.message || 'Market analysis failed'); } // v2.5
    finally { setMarketLoading(false); } // v2.5
  };

  // v2.5: Finance Projection handler → POST /api/ai-agents/FINANCE/run
  const handleProjectFinance = async () => { // v2.5
    setFinanceLoading(true); setFinanceError(null); setFinanceResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/FINANCE/run', { areaHectares: parseFloat(financeInput.areaHectares) || undefined, expectedYieldKg: parseFloat(financeInput.yieldKgPerHa) * (parseFloat(financeInput.areaHectares) || 1) || undefined, pricePerKg: parseFloat(financeInput.pricePerKg) || undefined, region: 'Cameroon' }); // v3.2.13: fixed field name yieldKgPerHa → expectedYieldKg (total kg = per-ha × area)
      setFinanceResult(res.data); // v2.5
    } catch (err: any) { setFinanceError(err.response?.data?.error || err.message || 'Finance projection failed'); } // v2.5
    finally { setFinanceLoading(false); } // v2.5
  };

  // v2.5: Executive Dashboard handler → POST /api/ai-agents/EXECUTIVE/run
  const handleGenerateExecutive = async () => { // v2.5
    setExecutiveLoading(true); setExecutiveError(null); setExecutiveResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/EXECUTIVE/run', { region: 'Cameroon' }); // v2.5
      setExecutiveResult(res.data); // v2.5
    } catch (err: any) { setExecutiveError(err.response?.data?.error || err.message || 'Executive summary failed'); } // v2.5
    finally { setExecutiveLoading(false); } // v2.5
  };

  // v2.5: helper — render confidence score as a colored pill (0-100 scale)
  const renderAIConfidence = (confidence: any) => { // v2.5
    if (confidence === undefined || confidence === null) return null; // v2.5
    const pct = Math.round(Number(confidence)); // v2.5
    if (isNaN(pct)) return null; // v2.5
    const tone: 'success' | 'warning' | 'danger' = pct >= 75 ? 'success' : pct >= 50 ? 'warning' : 'danger'; // v2.5
    return <StatusPill tone={tone} dot>Confidence {pct}%</StatusPill>; // v2.5
  };

  // Pagination
  const txPag = usePagination(transactions, 20);
  const pendingPag = usePagination(pendingBatches, 15);
  const wdPag = usePagination(withdrawals, 15);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const safeGet = async (url: string) => {
        try { return await apiClient.get(url); } catch { return { data: [] }; }
      };
      // v1.6.3: Finance should ONLY see INTAKED batches (officially graded at warehouse).
      // Batches that are PENDING (not yet intaked) are NOT eligible for payment.
      const [statsRes, intakedBatchesRes, wdRes, txRes, priceRes] = await Promise.all([
        safeGet('/mel/dashboard'),
        safeGet('/batches?status=INTAKED&limit=100'),
        safeGet('/withdrawals?limit=100'),
        safeGet('/transactions?limit=100'),
        safeGet('/settings'),
      ]);
      // v1.7.3 (M-04): graceful fallback when /mel/dashboard returns no data
      // (previously the Overview tab would disappear entirely on stats failure)
      if (statsRes.data && typeof statsRes.data === 'object' && Object.keys(statsRes.data).length > 0) {
        setStats(statsRes.data);
        setStatsError(false);
      } else {
        setStats(null);
        setStatsError(true);
      }
      // Payments tab: only show INTAKED batches (ready for payment after warehouse grading)
      const intakedBatches = Array.isArray(intakedBatchesRes.data) ? intakedBatchesRes.data : (intakedBatchesRes.data?.batches || []);
      setPendingBatches(intakedBatches);
      // Withdrawals and transactions: handle both array and object shapes
      setWithdrawals(Array.isArray(wdRes.data) ? wdRes.data : (wdRes.data?.withdrawals || wdRes.data?.data || []));
      setTransactions(Array.isArray(txRes.data) ? txRes.data : (txRes.data?.transactions || txRes.data?.data || []));
      if (priceRes.data?.price_per_kg) setPricePerKg(priceRes.data.price_per_kg);

      // v1.7.3 (M-05): warm chain stats in the background (non-blocking — failures are swallowed)
      safeGet('/chain/stats').then((r: any) => {
        if (r?.data?.stats) setChainStats(r.data.stats);
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load finance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Actions ──────────────────────────────────────────────
  const approvePayment = async (batchId: string) => {
    setError(null); setSuccess(null);
    try {
      const res = await apiClient.patch(`/batches/${batchId}/pay`, {});
      const ref = res.data?.transaction_reference || '—';
      const amount = res.data?.amount ? Number(res.data.amount).toLocaleString() : '';
      const inputDeducted = res.data?.input_credit_deducted ? Number(res.data.input_credit_deducted).toLocaleString() : null;
      const netPayout = res.data?.net_payout ? Number(res.data.net_payout).toLocaleString() : null;
      setSuccess(
        `Payment approved${amount ? `: ${amount} XAF` : ''}. Transaction reference: ${ref}` +
        (inputDeducted ? `\n\n💰 Input credit auto-deducted: ${inputDeducted} XAF. Net payout to farmer: ${netPayout} XAF.` : '')
      );
      setPaymentSheet(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Payment approval failed');
    }
  };

  const processWithdrawal = async (wdId: string, action: 'approve' | 'reject') => {
    setError(null); setSuccess(null);
    try {
      await apiClient.patch(`/withdrawals/${wdId}/process`, {
        status: action === 'approve' ? 'COMPLETED' : 'REJECTED',
      });
      setSuccess(`Withdrawal ${action}d`);
      setWithdrawalSheet(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || `Withdrawal ${action} failed`);
    }
  };

  const updatePrice = async () => {
    setError(null); setSuccess(null);
    // v1.7.3 (L-02): validate price per kg — must be a positive number
    const priceNum = Number(pricePerKg);
    if (!pricePerKg || isNaN(priceNum) || priceNum <= 0) {
      setError('Price per kg must be a positive number greater than 0.');
      return;
    }
    if (priceNum > 100000) {
      setError('Price per kg seems unusually high (>100,000 XAF). Please verify before saving.');
      return;
    }
    try {
      await apiClient.put('/settings', { price_per_kg: pricePerKg });
      setSuccess('Price per kg updated');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Price update failed');
    }
  };

  // v1.7.3 (M-06): export with error handling — falls back to error toast if endpoint fails
  const exportTransactions = () => {
    try {
      window.open('/api/transactions/export', '_blank');
    } catch (err: any) {
      setError('Export failed: ' + (err.message || 'could not open export URL'));
    }
  };

  // v1.7.3 (M-02): filtered lists for search — original state arrays preserved unchanged
  const filteredPendingBatches = useMemo(() => {
    if (!paymentsSearch.trim()) return pendingBatches;
    const q = paymentsSearch.trim().toLowerCase();
    return pendingBatches.filter((b: any) =>
      (b.farmer_name || '').toLowerCase().includes(q) ||
      (b.id || '').toLowerCase().includes(q)
    );
  }, [pendingBatches, paymentsSearch]);
  const filteredPendingPag = usePagination(filteredPendingBatches, 15);

  const filteredWithdrawals = useMemo(() => {
    if (!withdrawalsSearch.trim()) return withdrawals;
    const q = withdrawalsSearch.trim().toLowerCase();
    return withdrawals.filter((w: any) =>
      (w.farmer_name || '').toLowerCase().includes(q) ||
      (w.phone || '').toLowerCase().includes(q) ||
      (w.method || '').toLowerCase().includes(q)
    );
  }, [withdrawals, withdrawalsSearch]);
  const filteredWdPag = usePagination(filteredWithdrawals, 15);

  // v1.7.3 (L-01): filter transactions by date AND search term
  const filteredTransactions = useMemo(() => {
    let result = transactions;
    // Date filter
    if (txDateFilter !== 'all') {
      const days = parseInt(txDateFilter);
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      result = result.filter((t: any) =>
        t.created_at && new Date(t.created_at).getTime() >= cutoff
      );
    }
    // Search filter
    if (transactionsSearch.trim()) {
      const q = transactionsSearch.trim().toLowerCase();
      result = result.filter((t: any) =>
        (t.farmer_name || '').toLowerCase().includes(q) ||
        (t.batch_id || '').toLowerCase().includes(q) ||
        (t.transaction_id || t.reference || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [transactions, txDateFilter, transactionsSearch]);
  const filteredTxPag = usePagination(filteredTransactions, 20);

  // v1.7.3 (L-03): count withdrawals processed today (for badge in tab label)
  const withdrawalsProcessedToday = withdrawals.filter((w: any) =>
    w.processed_at && w.status !== 'PENDING' &&
    new Date(w.processed_at).toDateString() === new Date().toDateString()
  ).length;

  // v1.7.3 (M-03): tab change handler with brief skeleton
  const handleTabChange = (t: any) => {
    if (t === activeTab) return;
    setTabLoading(true);
    setActiveTab(t as Tab);
    setTimeout(() => setTabLoading(false), 200);
  };

  // ─── Render ───────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="cargo" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Loading finance workspace…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="finance-enterprise min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* v1.7.3 (M-01): WCAG 2.2 AA skip-to-content link */}
      <a href="#finance-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">
        Skip to content
      </a>
      {/* ─── Top bar ─── */}
      <header className="topbar finance-enterprise-topbar">
        <div className="finance-enterprise-topbar-inner max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="cargo" />
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">Finance workspace</h1>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">
                {user?.name} · {user?.role}
                {/* v1.7.3 (M-05): show pending decisions counter from chain stats */}
                {chainStats && Number(chainStats.pending_decisions) > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-400 text-2xs font-semibold">
                    {chainStats.pending_decisions} pending decisions
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={fetchData} disabled={refreshing} className="btn-secondary text-xs hide-mobile">
              <Icon.Refresh className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Syncing…' : 'Refresh'}</span>
            </button>
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing} className="md:hidden">
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            <ProfileButton />
            <LogoutButton />
          </div>
        </div>
      </header>

      <main id="finance-main-content" className="finance-enterprise-main max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-5">
        {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Pass 18: operational queue strip — existing state only; navigation uses the existing tab handler. */}
        <section className="finance-ops-strip" aria-label="Finance work queues">
          <button type="button" onClick={() => handleTabChange('payments')} className="finance-ops-card">
            <span className="finance-ops-icon" aria-hidden><Icon.Dollar /></span>
            <span><small>Pending payments</small><strong className="nums">{pendingBatches.length}</strong></span>
            <span className="finance-ops-action">Review</span>
          </button>
          <button type="button" onClick={() => handleTabChange('withdrawals')} className="finance-ops-card">
            <span className="finance-ops-icon" aria-hidden><Icon.Wallet /></span>
            <span><small>Pending withdrawals</small><strong className="nums">{withdrawals.filter(w => w.status === 'PENDING').length}</strong></span>
            <span className="finance-ops-action">Review</span>
          </button>
          <button type="button" onClick={() => handleTabChange('overview')} className="finance-ops-card">
            <span className="finance-ops-icon" aria-hidden><Icon.Trending /></span>
            <span><small>Pending decisions</small><strong className="nums">{Number(chainStats?.pending_decisions || 0)}</strong></span>
            <span className="finance-ops-action">Overview</span>
          </button>
          <button type="button" onClick={() => handleTabChange('transactions')} className="finance-ops-card">
            <span className="finance-ops-icon" aria-hidden><Icon.Check /></span>
            <span><small>Processed today</small><strong className="nums">{withdrawalsProcessedToday}</strong></span>
            <span className="finance-ops-action">Ledger</span>
          </button>
        </section>

        {/* ─── Tabs ─── */}
        <SegmentedTabs
          items={[
            { id: 'overview', label: 'Overview', icon: <Icon.Trending className="w-4 h-4" /> },
            { id: 'payments', label: `Payments (${pendingBatches.length})`, icon: <Icon.Dollar className="w-4 h-4" /> },
            // v1.7.3 (L-03): withdrawals badge also shows today's processed count
            { id: 'withdrawals', label: `Withdrawals (${withdrawals.filter(w => w.status === 'PENDING').length}${withdrawalsProcessedToday > 0 ? ` · ${withdrawalsProcessedToday} today` : ''})`, icon: <Icon.Wallet className="w-4 h-4" /> },
            { id: 'transactions', label: `Transactions (${transactions.length})`, icon: <Icon.Dollar className="w-4 h-4" /> },
            { id: 'reports', label: 'Reports', icon: <Icon.Download className="w-4 h-4" /> },
            // v2.5: AI Insights tab — 3 AI agents for finance role (Market, Finance, Executive)
            { id: 'ai-insights', label: 'AI Insights', icon: <Icon.Trending className="w-4 h-4" /> }, // v2.5
          ]}
          active={activeTab}
          onChange={handleTabChange}
        />

        {/* v1.7.3 (M-03): loading skeleton shown briefly when switching tabs */}
        {tabLoading ? (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-40" />
            </div>
            <div className="p-5 space-y-2">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          </Card>
        ) : (
        <>

        {/* ─── Overview Tab ─── */}
        {/* v1.7.3 (M-04): render overview even if stats failed — show fallback KPIs from local data */}
        {activeTab === 'overview' && (
          <div className="finance-overview space-y-4">
            {statsError && (
              <Alert tone="warning">Stats endpoint unavailable — showing limited KPIs from local data.</Alert>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Total Farmers" value={stats?.total_farmers || 0} icon={<Icon.Dollar className="w-4 h-4" />} />
              <MetricTile label="Total Batches" value={stats?.total_batches || 0} />
              <MetricTile label="Delivered" value={stats?.total_delivered || 0} />
              <MetricTile label="Avg Price/kg" value={`${Number(stats?.avg_price_per_kg || pricePerKg).toLocaleString()} XAF`} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Pending Payments" value={pendingBatches.length} accent="cargo" icon={<Icon.Wallet className="w-4 h-4" />} />
              <MetricTile label="Pending Withdrawals" value={withdrawals.filter(w => w.status === 'PENDING').length} accent="cargo" />
              <MetricTile label="Total Disbursed" value={`${Number(stats?.total_payout || 0).toLocaleString()} XAF`} />
              <MetricTile label="Zero-Waste Score" value={stats?.avg_zero_waste_score || 0} />
            </div>

            <Card padding="lg">
              <SectionHeader eyebrow="Quick actions" title="Finance Operations" />
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <button onClick={() => setActiveTab('payments')} className="btn-secondary text-sm justify-start">
                  <Icon.Dollar className="w-4 h-4" /> Approve {pendingBatches.length} pending payments
                </button>
                <button onClick={() => setActiveTab('withdrawals')} className="btn-secondary text-sm justify-start">
                  <Icon.Wallet className="w-4 h-4" /> Process {withdrawals.filter(w => w.status === 'PENDING').length} withdrawals
                </button>
                <button onClick={exportTransactions} className="btn-secondary text-sm justify-start">
                  <Icon.Download className="w-4 h-4" /> Export transactions CSV
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* ─── Payments Tab ─── */}
        {activeTab === 'payments' && (
          <Card padding="none" className="finance-payments-card overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader eyebrow="Pending batch payments" title="Approve Payments" subtitle="Batches awaiting mobile money disbursement" />
              {/* v1.7.3 (M-02): search/filter input */}
              {pendingBatches.length > 0 && (
                <div className="mt-3 relative">
                  <Icon.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input
                    type="search"
                    value={paymentsSearch}
                    onChange={(e) => { setPaymentsSearch(e.target.value); filteredPendingPag.setPage(1); }}
                    placeholder="Search by farmer name or batch ID…"
                    className="input pl-9 text-sm"
                    aria-label="Search pending payments"
                  />
                </div>
              )}
            </div>
            {pendingBatches.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Check className="w-6 h-6" />} title="No pending payments" description="All batches have been paid." /></div>
            ) : filteredPendingBatches.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Search className="w-6 h-6" />} title="No matches" description={`No payments match "${paymentsSearch}"`} /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table finance-money-table">
                  <thead><tr><th>Batch ID</th><th>Farmer</th><th>Weight (kg)</th><th>Grade</th><th className="finance-money-col">Amount (XAF)</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredPendingPag.paginatedItems.map((b: any) => (
                      <tr key={b.id}>
                        <td className="text-xs font-mono">{b.id.slice(0, 8)}…</td>
                        <td className="text-sm">{b.farmer_name || b.farmer_id?.slice(0, 8) || '—'}</td>
                        <td className="nums">{b.actual_weight || b.est_weight}</td>
                        <td><StatusPill tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'} dot>{b.quality_grade}</StatusPill></td>
                        <td className="nums font-medium finance-money-col">{Number(b.calculated_amount || 0).toLocaleString()}</td>
                        <td><SmartStatus status={b.payment_status} /></td>
                        <td>
                          <button onClick={() => setPaymentSheet(b)} className="btn-secondary text-xs">
                            <Icon.Check className="w-3 h-3" /> Approve
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={paymentsSearch.trim() ? filteredPendingPag.page : pendingPag.page}
                pageSize={paymentsSearch.trim() ? filteredPendingPag.pageSize : pendingPag.pageSize}
                total={paymentsSearch.trim() ? filteredPendingPag.total : pendingPag.total}
                onPageChange={paymentsSearch.trim() ? filteredPendingPag.setPage : pendingPag.setPage}
                onPageSizeChange={paymentsSearch.trim() ? filteredPendingPag.setPageSize : pendingPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ─── Withdrawals Tab ─── */}
        {activeTab === 'withdrawals' && (
          <Card padding="none" className="finance-withdrawals-card overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader eyebrow="Farmer withdrawal requests" title="Process Withdrawals" subtitle="Approve or reject mobile money withdrawal requests" />
              {/* v1.7.3 (M-02): search/filter input */}
              {withdrawals.length > 0 && (
                <div className="mt-3 relative">
                  <Icon.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input
                    type="search"
                    value={withdrawalsSearch}
                    onChange={(e) => { setWithdrawalsSearch(e.target.value); filteredWdPag.setPage(1); }}
                    placeholder="Search by farmer name, phone, or method…"
                    className="input pl-9 text-sm"
                    aria-label="Search withdrawals"
                  />
                </div>
              )}
            </div>
            {withdrawals.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Wallet className="w-6 h-6" />} title="No withdrawal requests" /></div>
            ) : filteredWithdrawals.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Search className="w-6 h-6" />} title="No matches" description={`No withdrawals match "${withdrawalsSearch}"`} /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Farmer</th><th>Amount (XAF)</th><th>Method</th><th>Phone</th><th>Requested</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredWdPag.paginatedItems.map((w: any) => (
                      <tr key={w.id}>
                        <td className="text-sm">{w.farmer_name || w.farmer_id?.slice(0, 8) || '—'}</td>
                        <td className="nums font-medium finance-money-col">{Number(w.amount || 0).toLocaleString()}</td>
                        <td><StatusPill tone="info">{w.method || 'MTN'}</StatusPill></td>
                        <td className="text-xs font-mono">{w.phone || '—'}</td>
                        <td className="text-2xs">{w.created_at ? new Date(w.created_at).toLocaleDateString() : '—'}</td>
                        <td><SmartStatus status={w.status} /></td>
                        <td>
                          {w.status === 'PENDING' ? (
                            <button onClick={() => setWithdrawalSheet(w)} className="btn-secondary text-xs">Process</button>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={withdrawalsSearch.trim() ? filteredWdPag.page : wdPag.page}
                pageSize={withdrawalsSearch.trim() ? filteredWdPag.pageSize : wdPag.pageSize}
                total={withdrawalsSearch.trim() ? filteredWdPag.total : wdPag.total}
                onPageChange={withdrawalsSearch.trim() ? filteredWdPag.setPage : wdPag.setPage}
                onPageSizeChange={withdrawalsSearch.trim() ? filteredWdPag.setPageSize : wdPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ─── Transactions Tab ─── */}
        {activeTab === 'transactions' && (
          <Card padding="none" className="finance-ledger-card overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 space-y-3">
              <div className="flex justify-between items-center">
                <SectionHeader eyebrow="Full ledger" title="Transactions" />
                <button onClick={exportTransactions} className="btn-secondary text-xs">
                  <Icon.Download className="w-3.5 h-3.5" /> Export CSV
                </button>
              </div>
              {/* v1.7.3 (L-01): date filter buttons + v1.7.3 (M-02): search input */}
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex gap-1" role="group" aria-label="Date filter">
                  {([['all','All time'],['7','7 days'],['30','30 days'],['90','90 days']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      aria-pressed={txDateFilter === val}
                      onClick={() => { setTxDateFilter(val as any); filteredTxPag.setPage(1); }}
                      className={`text-2xs px-2 py-1 rounded-md font-medium transition-colors ${
                        txDateFilter === val
                          ? 'bg-ink-900 text-white dark:bg-ink-50 dark:text-ink-900'
                          : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {transactions.length > 0 && (
                  <div className="relative flex-1 min-w-[200px]">
                    <Icon.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                    <input
                      type="search"
                      value={transactionsSearch}
                      onChange={(e) => { setTransactionsSearch(e.target.value); filteredTxPag.setPage(1); }}
                      placeholder="Search by farmer, batch ID, or reference…"
                      className="input pl-9 text-sm"
                      aria-label="Search transactions"
                    />
                  </div>
                )}
              </div>
            </div>
            {transactions.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Dollar className="w-6 h-6" />} title="No transactions yet" /></div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Search className="w-6 h-6" />} title="No matches" description="Try changing the date filter or search term." /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table finance-money-table">
                  <thead><tr><th>Date</th><th>Batch</th><th>Farmer</th><th className="finance-money-col">Amount (XAF)</th><th>Method</th><th>Status</th><th>Reference</th></tr></thead>
                  <tbody>
                    {filteredTxPag.paginatedItems.map((t: any) => (
                      <tr key={t.id}>
                        <td className="text-2xs">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
                        <td className="text-xs font-mono">{t.batch_id?.slice(0, 8) || '—'}</td>
                        <td className="text-sm">{t.farmer_name || '—'}</td>
                        <td className="nums font-medium finance-money-col">{Number(t.amount || 0).toLocaleString()}</td>
                        {/* v1.7.3 (L-04): normalized method column — carrier → method → type → '—' */}
                        <td><StatusPill tone="info">{t.carrier || t.method || t.type || '—'}</StatusPill></td>
                        <td><SmartStatus status={t.status} /></td>
                        <td className="text-xs font-mono">{(t.transaction_id || t.reference || '—').slice(0, 20)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={filteredTxPag.page}
                pageSize={filteredTxPag.pageSize}
                total={filteredTxPag.total}
                onPageChange={filteredTxPag.setPage}
                onPageSizeChange={filteredTxPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ─── Reports Tab ─── */}
        {activeTab === 'reports' && (
          <div className="finance-reports space-y-4">
            <Card padding="lg">
              <SectionHeader eyebrow="Configuration" title="Price per kg" subtitle="Sets the payout rate for all batch payments" />
              <div className="mt-4 flex gap-3 items-end">
                <div className="flex-1">
                  <label htmlFor="price" className="label">Price per kg (XAF)</label>
                  <input id="price" type="number" min="1" max="100000" value={pricePerKg} onChange={(e) => setPricePerKg(e.target.value)} className="input" />
                  {/* v1.7.3 (L-02): inline validation hint */}
                  {pricePerKg && (Number(pricePerKg) <= 0 || Number(pricePerKg) > 100000) && (
                    <p className="text-2xs text-danger-600 dark:text-danger-500 mt-1">Must be between 1 and 100,000 XAF.</p>
                  )}
                </div>
                <button onClick={() => { const next = Number(pricePerKg || 0).toLocaleString(); if (confirm(`Update payout price to ${next} XAF/kg?\n\nThis changes the rate used for batch payments.`)) updatePrice(); }} className="btn-primary">Update Price</button>
              </div>
            </Card>

            <Card padding="lg">
              <SectionHeader eyebrow="Exports" title="Financial Reports" subtitle="Download CSV reports for reconciliation and audits" />
              <div className="finance-reconciliation-note mt-3"><Icon.Alert className="w-4 h-4" /><span>These exports support reconciliation and audit review; this workspace does not infer a reconciled state unless the underlying data provides one.</span></div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* v1.7.3 (M-06): all exports now route through the error-handling exportTransactions wrapper */}
                <button onClick={() => exportTransactions()} className="btn-secondary text-sm justify-start">
                  <Icon.Download className="w-4 h-4" /> Transaction Ledger (CSV)
                </button>
                <button onClick={() => { try { window.open('/api/mel/export', '_blank'); } catch (e: any) { setError('MEL export failed: ' + (e.message || 'unknown')); } }} className="btn-secondary text-sm justify-start">
                  <Icon.Download className="w-4 h-4" /> MEL Dashboard Export (CSV)
                </button>
                <button onClick={() => { try { window.open('/api/audit-log/export', '_blank'); } catch (e: any) { setError('Audit log export failed: ' + (e.message || 'unknown')); } }} className="btn-secondary text-sm justify-start">
                  <Icon.Download className="w-4 h-4" /> Audit Log (CSV)
                </button>
                <button onClick={() => { try { window.open('/api/batches/export', '_blank'); } catch (e: any) { setError('Batches export failed: ' + (e.message || 'unknown')); } }} className="btn-secondary text-sm justify-start">
                  <Icon.Download className="w-4 h-4" /> Batches Export (CSV)
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* ─── v2.5: AI Insights Tab — 3 AI agents (Market, Finance, Executive) ─── */}
        {activeTab === 'ai-insights' && ( // v2.5
          <div className="finance-ai-insights space-y-4"> {/* v2.5 */}
            {/* v2.5: intro banner */}
            <Card padding="lg"> {/* v2.5 */}
              <SectionHeader // v2.5
                eyebrow="v2.5 AI Agents" // v2.5
                title="AI Insights" // v2.5
                subtitle="Advisory analysis for market intelligence, finance projections, and executive summaries. Operational approvals and transaction truth remain in the finance workflows." // v2.5
                icon={<Icon.Trending className="w-4 h-4" />} // v2.5
              />
            </Card>

            {/* v2.5: ═══ Market Intelligence ═══ */}
            <Card padding="lg"> {/* v2.5 */}
              <SectionHeader // v2.5
                eyebrow="Agent 1 · MARKET" // v2.5
                title="Market Intelligence" // v2.5
                subtitle="Analyze Cameroon cocoa market conditions, price trends, and demand outlook." // v2.5
                icon={<Icon.Trending className="w-4 h-4" />} // v2.5
              />
              <div className="mt-4 space-y-3"> {/* v2.5 */}
                <button onClick={handleAnalyzeMarket} disabled={marketLoading} className="btn-primary text-sm"> {/* v2.5 */}
                  {marketLoading ? <><Spinner size="sm" /> Analyzing…</> : <><Icon.Trending className="w-4 h-4" /> Analyze Market</>} {/* v2.5 */}
                </button>
                {marketError && <Alert tone="danger" onClose={() => setMarketError(null)}>{marketError}</Alert>} {/* v2.5 */}
                {marketResult && ( // v2.5
                  <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
                    <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                      <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50"> {/* v2.5 */}
                        {marketResult.output?.market_outlook || marketResult.output?.summary || marketResult.output?.raw_text?.slice(0, 80) || 'Market analysis'} {/* v2.5 */}
                      </h4>
                      {renderAIConfidence(marketResult.output?.confidence)} {/* v2.5 */}
                    </div>
                    {marketResult.output?.current_price && <p className="text-sm"><span className="text-ink-500">Current price:</span> <span className="font-medium">{marketResult.output.current_price}</span></p>} {/* v2.5 */}
                    {marketResult.output?.trend && <p className="text-sm"><span className="text-ink-500">Trend:</span> <span className="font-medium">{marketResult.output.trend}</span></p>} {/* v2.5 */}
                    {marketResult.output?.forecast && <p className="text-sm text-ink-700 dark:text-ink-300">{marketResult.output.forecast}</p>} {/* v2.5 */}
                    {marketResult.output?.key_drivers && Array.isArray(marketResult.output.key_drivers) && ( // v2.5
                      <div> {/* v2.5 */}
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Key drivers</p> {/* v2.5 */}
                        <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                          {marketResult.output.key_drivers.map((d: string, i: number) => <li key={i}>{d}</li>)} {/* v2.5 */}
                        </ul>
                      </div>
                    )}
                    {marketResult.output?.recommendations && Array.isArray(marketResult.output.recommendations) && ( // v2.5
                      <div> {/* v2.5 */}
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Recommendations</p> {/* v2.5 */}
                        <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                          {marketResult.output.recommendations.map((r: string, i: number) => <li key={i}>{r}</li>)} {/* v2.5 */}
                        </ul>
                      </div>
                    )}
                    {marketResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{marketResult.output.reasoning}</p>} {/* v2.5 */}
                    {marketResult.output?.raw_text && !marketResult.output.market_outlook && !marketResult.output.summary && <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-wrap">{marketResult.output.raw_text}</p>} {/* v2.5 */}
                    {!marketResult.success && marketResult.error && <Alert tone="danger">{marketResult.error}</Alert>} {/* v2.5 */}
                  </div>
                )}
              </div>
            </Card>

            {/* v2.5: ═══ Finance Projection ═══ */}
            <Card padding="lg"> {/* v2.5 */}
              <SectionHeader // v2.5
                eyebrow="Agent 2 · FINANCE" // v2.5
                title="Finance Projection" // v2.5
                subtitle="Project revenue from area, expected yield, and farm-gate price." // v2.5
                icon={<Icon.Dollar className="w-4 h-4" />} // v2.5
              />
              <div className="mt-4 space-y-3"> {/* v2.5 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3"> {/* v2.5 */}
                  <div> {/* v2.5 */}
                    <label className="label">Area (hectares)</label> {/* v2.5 */}
                    <input type="number" min="0" step="0.1" className="input" value={financeInput.areaHectares} onChange={e => setFinanceInput({ ...financeInput, areaHectares: e.target.value })} /> {/* v2.5 */}
                  </div>
                  <div> {/* v2.5 */}
                    <label className="label">Expected yield (kg/ha)</label> {/* v2.5 */}
                    <input type="number" min="0" step="1" className="input" value={financeInput.yieldKgPerHa} onChange={e => setFinanceInput({ ...financeInput, yieldKgPerHa: e.target.value })} /> {/* v2.5 */}
                  </div>
                  <div> {/* v2.5 */}
                    <label className="label">Farm-gate price (XAF/kg)</label> {/* v2.5 */}
                    <input type="number" min="0" step="50" className="input" value={financeInput.pricePerKg} onChange={e => setFinanceInput({ ...financeInput, pricePerKg: e.target.value })} /> {/* v2.5 */}
                  </div>
                </div>
                <button onClick={handleProjectFinance} disabled={financeLoading} className="btn-primary text-sm"> {/* v2.5 */}
                  {financeLoading ? <><Spinner size="sm" /> Projecting…</> : <><Icon.Dollar className="w-4 h-4" /> Project</>} {/* v2.5 */}
                </button>
                {financeError && <Alert tone="danger" onClose={() => setFinanceError(null)}>{financeError}</Alert>} {/* v2.5 */}
                {financeResult && ( // v2.5
                  <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
                    <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                      <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Finance projection</h4> {/* v2.5 */}
                      {renderAIConfidence(financeResult.output?.confidence)} {/* v2.5 */}
                    </div>
                    {financeResult.output?.gross_revenue_xaf !== undefined && ( // v2.5
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2"> {/* v2.5 */}
                        <div> {/* v2.5 */}
                          <p className="text-2xs uppercase tracking-micro text-ink-500">Gross revenue</p> {/* v2.5 */}
                          <p className="text-lg font-bold nums">{Number(financeResult.output.gross_revenue_xaf).toLocaleString()} XAF</p> {/* v2.5 */}
                        </div>
                        {financeResult.output?.net_profit_xaf !== undefined && ( // v2.5
                          <div> {/* v2.5 */}
                            <p className="text-2xs uppercase tracking-micro text-ink-500">Net profit</p> {/* v2.5 */}
                            <p className="text-lg font-bold nums text-success-600">{Number(financeResult.output.net_profit_xaf).toLocaleString()} XAF</p> {/* v2.5 */}
                          </div>
                        )}
                        {financeResult.output?.roi_pct !== undefined && ( // v2.5
                          <div> {/* v2.5 */}
                            <p className="text-2xs uppercase tracking-micro text-ink-500">ROI</p> {/* v2.5 */}
                            <p className="text-lg font-bold nums">{Number(financeResult.output.roi_pct).toFixed(1)}%</p> {/* v2.5 */}
                          </div>
                        )}
                      </div>
                    )}
                    {financeResult.output?.cost_breakdown && Array.isArray(financeResult.output.cost_breakdown) && ( // v2.5
                      <div> {/* v2.5 */}
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Cost breakdown</p> {/* v2.5 */}
                        <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                          {financeResult.output.cost_breakdown.map((c: string, i: number) => <li key={i}>{c}</li>)} {/* v2.5 */}
                        </ul>
                      </div>
                    )}
                    {financeResult.output?.recommendations && Array.isArray(financeResult.output.recommendations) && ( // v2.5
                      <div> {/* v2.5 */}
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Recommendations</p> {/* v2.5 */}
                        <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                          {financeResult.output.recommendations.map((r: string, i: number) => <li key={i}>{r}</li>)} {/* v2.5 */}
                        </ul>
                      </div>
                    )}
                    {financeResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{financeResult.output.reasoning}</p>} {/* v2.5 */}
                    {financeResult.output?.raw_text && financeResult.output.gross_revenue_xaf === undefined && <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-wrap">{financeResult.output.raw_text}</p>} {/* v2.5 */}
                    {!financeResult.success && financeResult.error && <Alert tone="danger">{financeResult.error}</Alert>} {/* v2.5 */}
                  </div>
                )}
              </div>
            </Card>

            {/* v2.5: ═══ Executive Dashboard ═══ */}
            <Card padding="lg"> {/* v2.5 */}
              <SectionHeader // v2.5
                eyebrow="Agent 3 · EXECUTIVE" // v2.5
                title="Executive Dashboard" // v2.5
                subtitle="Generate an executive summary across all CoVECA operational and financial metrics." // v2.5
                icon={<Icon.Trending className="w-4 h-4" />} // v2.5
              />
              <div className="mt-4 space-y-3"> {/* v2.5 */}
                <button onClick={handleGenerateExecutive} disabled={executiveLoading} className="btn-primary text-sm"> {/* v2.5 */}
                  {executiveLoading ? <><Spinner size="sm" /> Generating…</> : <><Icon.Trending className="w-4 h-4" /> Generate Executive Summary</>} {/* v2.5 */}
                </button>
                {executiveError && <Alert tone="danger" onClose={() => setExecutiveError(null)}>{executiveError}</Alert>} {/* v2.5 */}
                {executiveResult && ( // v2.5
                  <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
                    <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                      <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50"> {/* v2.5 */}
                        {executiveResult.output?.headline || executiveResult.output?.title || 'Executive summary'} {/* v2.5 */}
                      </h4>
                      {renderAIConfidence(executiveResult.output?.confidence)} {/* v2.5 */}
                    </div>
                    {executiveResult.output?.summary && <p className="text-sm text-ink-700 dark:text-ink-300">{executiveResult.output.summary}</p>} {/* v2.5 */}
                    {executiveResult.output?.key_metrics && Array.isArray(executiveResult.output.key_metrics) && ( // v2.5
                      <div> {/* v2.5 */}
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Key metrics</p> {/* v2.5 */}
                        <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                          {executiveResult.output.key_metrics.map((m: string, i: number) => <li key={i}>{m}</li>)} {/* v2.5 */}
                        </ul>
                      </div>
                    )}
                    {executiveResult.output?.risks && Array.isArray(executiveResult.output.risks) && ( // v2.5
                      <div> {/* v2.5 */}
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Risks</p> {/* v2.5 */}
                        <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                          {executiveResult.output.risks.map((r: string, i: number) => <li key={i}>{r}</li>)} {/* v2.5 */}
                        </ul>
                      </div>
                    )}
                    {executiveResult.output?.recommendations && Array.isArray(executiveResult.output.recommendations) && ( // v2.5
                      <div> {/* v2.5 */}
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Recommendations</p> {/* v2.5 */}
                        <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                          {executiveResult.output.recommendations.map((r: string, i: number) => <li key={i}>{r}</li>)} {/* v2.5 */}
                        </ul>
                      </div>
                    )}
                    {executiveResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{executiveResult.output.reasoning}</p>} {/* v2.5 */}
                    {executiveResult.output?.raw_text && !executiveResult.output.summary && <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-wrap">{executiveResult.output.raw_text}</p>} {/* v2.5 */}
                    {!executiveResult.success && executiveResult.error && <Alert tone="danger">{executiveResult.error}</Alert>} {/* v2.5 */}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* end v1.7.3 (M-03) tabLoading conditional wrapper */}
        </>
        )}
      </main>

      {/* ─── Payment Approval Sheet ─── */}
      <Sheet open={!!paymentSheet} onClose={() => setPaymentSheet(null)} title="Approve Payment"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setPaymentSheet(null)} className="btn-secondary flex-1">Cancel</button>
            {/* v1.7.3 (C-01): confirmation dialog before irreversible mobile money disbursement */}
            <button
              onClick={() => {
                const amount = Number(paymentSheet?.calculated_amount || 0).toLocaleString();
                const farmer = paymentSheet?.farmer_name || 'this farmer';
                if (confirm(`Approve payment of ${amount} XAF to ${farmer}?\n\nThis will trigger a real mobile money disbursement and CANNOT be undone.`)) {
                  approvePayment(paymentSheet?.id);
                }
              }}
              className="btn-primary flex-1"
            >
              <Icon.Check className="w-4 h-4" /> Approve & Disburse
            </button>
          </div>
        }>
        {paymentSheet && (
          <div className="finance-money-sheet space-y-3">
            <div className="finance-money-sheet-safety"><Icon.Alert className="w-4 h-4" /><span>Review beneficiary, amount, and batch before irreversible disbursement.</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Batch</span><span className="text-sm font-mono">{paymentSheet.id?.slice(0, 8)}…</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Farmer</span><span className="text-sm">{paymentSheet.farmer_name || '—'}</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Weight</span><span className="text-sm nums">{paymentSheet.actual_weight || paymentSheet.est_weight} kg</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Grade</span><StatusPill tone={paymentSheet.quality_grade === 'A' ? 'success' : 'warning'}>{paymentSheet.quality_grade}</StatusPill></div>
            <div className="flex justify-between border-t border-ink-200 dark:border-ink-800 pt-3">
              <span className="text-sm font-medium">Amount</span>
              <span className="text-lg font-bold nums">{Number(paymentSheet.calculated_amount || 0).toLocaleString()} XAF</span>
            </div>
            <Alert tone="info">Clicking "Approve & Disburse" will trigger a mobile money payment to the farmer's registered phone number.</Alert>
          </div>
        )}
      </Sheet>

      {/* ─── Withdrawal Processing Sheet ─── */}
      <Sheet open={!!withdrawalSheet} onClose={() => setWithdrawalSheet(null)} title="Process Withdrawal"
        footer={
          <div className="flex gap-2">
            {/* v1.7.3 (C-02): confirmation dialog before approving withdrawal */}
            <button
              onClick={() => {
                const amount = Number(withdrawalSheet?.amount || 0).toLocaleString();
                const farmer = withdrawalSheet?.farmer_name || 'this farmer';
                if (confirm(`REJECT withdrawal of ${amount} XAF for ${farmer}?\n\nThe farmer will be notified that their request was rejected.`)) {
                  processWithdrawal(withdrawalSheet?.id, 'reject');
                }
              }}
              className="btn-secondary flex-1 text-danger-600"
            >
              <Icon.X className="w-4 h-4" /> Reject
            </button>
            <button
              onClick={() => {
                const amount = Number(withdrawalSheet?.amount || 0).toLocaleString();
                const farmer = withdrawalSheet?.farmer_name || 'this farmer';
                const phone = withdrawalSheet?.phone || 'their phone';
                if (confirm(`Approve withdrawal of ${amount} XAF for ${farmer}?\n\nFunds will be disbursed via mobile money to ${phone}. This CANNOT be undone.`)) {
                  processWithdrawal(withdrawalSheet?.id, 'approve');
                }
              }}
              className="btn-primary flex-1"
            >
              <Icon.Check className="w-4 h-4" /> Approve
            </button>
          </div>
        }>
        {withdrawalSheet && (
          <div className="finance-money-sheet space-y-3">
            <div className="finance-money-sheet-safety"><Icon.Alert className="w-4 h-4" /><span>Review request, amount, method, and phone before processing.</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Farmer</span><span className="text-sm">{withdrawalSheet.farmer_name || '—'}</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Amount</span><span className="text-sm nums font-medium">{Number(withdrawalSheet.amount || 0).toLocaleString()} XAF</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Method</span><span className="text-sm">{withdrawalSheet.method || 'MTN'}</span></div>
            <div className="flex justify-between"><span className="text-sm text-ink-500">Phone</span><span className="text-sm font-mono">{withdrawalSheet.phone || '—'}</span></div>
            <Alert tone="warning">Approving will disburse funds via mobile money. This action cannot be undone.</Alert>
          </div>
        )}
      </Sheet>
          <CopilotWidget userRole="finance" workspaceContext={activeTab} />
</div>
  );
};

export default FinanceDashboard;
